import { BottomTabNavigationOptions, createBottomTabNavigator } from "@react-navigation/bottom-tabs";
import { useNavigation } from "@react-navigation/native";
import type { TFunction } from "i18next";
import _ from "lodash";
import {
  Notebook,
  NotebookText,
  ScrollText,
  Settings,
  ShoppingBasket,
  User as UserIcon,
  Users,
} from "lucide-react-native";
import { Box, Pressable, Text, useTheme } from "native-base";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useSelector } from "react-redux";

import { Routes } from "../constants";
import { FontFamily, FontSize } from "../constants/fonts";
import { FeatureFlag, isFeatureFlagActive } from "../helpers/featureFlags";
import {
  areWeAnOpinionatedRecipeInspirationApp,
  getOrganisation,
  hasUserCompletedSimpleOnboarding,
  hasUserCompletedTheirDiyNutritionPlanningFormCompletely,
  isSimpleOrg,
  isUserDiy,
  shouldB2CCustomerHavePaidAccess,
  shouldShowProgressTab,
  userAccountIsDisabledOrHasInValidSubscription,
} from "../helpers/userHelpers";
import { CoachModeViewClientsScreen } from "../screens";
import BillingIssueScreen from "../screens/BillingIssueScreen";
import CoachModeAccountScreen from "../screens/CoachModeAccountScreen";
import DiaryViewScreen from "../screens/DiaryViewScreen";
import GroceryListScreen from "../screens/GroceryListScreen";
import MyProfileScreen from "../screens/MyProfileScreen";
import MyProgressScreen from "../screens/MyProgressScreen";
import DIY1GoalScreen from "../screens/New_DIY_form/Onboarding_2_GoalScreen";
import NutritionalPlanScreen from "../screens/New_DIY_form/Onboarding_7_NutritionPlanScreen";
import RecipeInspirationScreen from "../screens/RecipeInspirationScreen";
import SimpleRecipeInspirationScreen from "../screens/SimpleRecipeInspirationScreen";
import type { User } from "../services/backendTypes";
import { clientModeSelector, userSelector } from "../slices/userSlice";
import type { BottomNavigationTabOptions } from "./NavigationStackParams";

const DELINQUENT_SUBSCRIPTION_SCREENS = [Routes.BillingIssueScreen];

const CLIENT_VIEW_SCREENS: BottomNavigationTabOptions[] = [
  Routes.RecipeInspirationScreen,
  Routes.SimpleRecipeInspirationScreen,
  Routes.MyProgressScreen,
  Routes.DiaryViewScreen,
  Routes.GroceryListScreen,
  Routes.MyProfileScreen,
  Routes.BillingIssueScreen,
];

const COACH_VIEW_SCREENS: BottomNavigationTabOptions[] = [
  Routes.CoachModeViewClientsScreen,
  Routes.CoachModeAccountScreen,
  Routes.MyProfileScreen,
  Routes.BillingIssueScreen,
];

const ICONS_LOOKUP: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [B in BottomNavigationTabOptions]: React.ComponentType<any>;
} = {
  [Routes.DiaryViewScreen]: Notebook,
  [Routes.MyProgressScreen]: NotebookText, // Using NotebookText for progress as well
  [Routes.MyProfileScreen]: UserIcon,
  [Routes.CoachModeAccountScreen]: Settings,
  [Routes.GroceryListScreen]: ShoppingBasket,
  [Routes.RecipeInspirationScreen]: ScrollText,
  [Routes.SimpleRecipeInspirationScreen]: ScrollText,

  // Coach-mode
  [Routes.CoachModeViewClientsScreen]: Users,
  [Routes.BillingIssueScreen]: Settings,

  // Diy
  [Routes.DIY1GoalScreen]: ScrollText,
  [Routes.NutritionalPlanScreen]: ScrollText,
};

function CustomTabButton({
  focused,
  screenName,
  title,
  onPress,
}: {
  focused: boolean;
  screenName: BottomNavigationTabOptions;
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onPress?: (e: any) => void;
}): JSX.Element {
  const theme = useTheme();

  return (
    <Pressable onPress={onPress} flex={1} alignItems="center" justifyContent="center" py="1">
      <Box
        alignItems="center"
        justifyContent="center"
        py="1"
        px="2"
        borderRadius={theme.borderRadii.button.default}
        bg={focused ? "primary.50" : "transparent"}
      >
        {React.createElement(ICONS_LOOKUP[screenName], {
          size: 20,
          color: focused ? theme.colors.white : theme.colors.function["50"],
          strokeWidth: 1.25,
          style: { marginBottom: 4 },
        })}
        <Text fontSize="xs" fontFamily="body" color={focused ? "white" : "function.50"} textAlign="center">
          {title}
        </Text>
      </Box>
    </Pressable>
  );
}

function getTitleForTab(route: BottomNavigationTabOptions, t: TFunction): string {
  switch (route) {
    // Client-mode screens
    case Routes.DiaryViewScreen:
      return t("bottom_tabs.planner_tab_name");
    // NOTE: Not currently implemented
    // case Routes.RecipesScreen:
    //   return t("Recipes");
    case Routes.RecipeInspirationScreen:
      return t("bottom_tabs.recipes_tab_name");
    case Routes.SimpleRecipeInspirationScreen:
      return t("bottom_tabs.recipes_tab_name");
    case Routes.MyProgressScreen:
      return t("bottom_tabs.progress_tab_name");
    case Routes.GroceryListScreen:
      return t("bottom_tabs.groceries_tab_name");
    case Routes.MyProfileScreen:
      return t("bottom_tabs.my_profile_tab_name");
    case Routes.CoachModeAccountScreen:
      return t("bottom_tabs.account_management_tab_name");
    case Routes.BillingIssueScreen:
      return t("bottom_tabs.billing_issue_tab_name");

    // Coach-mode screens
    case Routes.CoachModeViewClientsScreen:
      return t("bottom_tabs.clients_tab_name");

    case Routes.DIY1GoalScreen:
      return t("general.welcome");
    case Routes.NutritionalPlanScreen:
      // NOTE: This is valid for the initial onboarding flow but not subsequently -
      // we may want to change this but there is no state that we can use to detect this at the moment.
      return t("general.welcome");

    default:
      throw new Error(`Route "${String(route)}" is not implemented for BottomTabNavigator`);
  }
}

const doNotDisplayTab = { tabBarButton: () => null };

function hasUserCompletedUserProfileButDoesNotHaveWeeklyNutritionPlan(user: User): boolean {
  return Boolean(user.intake && !user.intake.weekly_nutrition_plan);
}

// Helper function to determine if Recipe Inspiration is available
const isRecipeInspirationAvailable = (user: User | null, clientMode: boolean): boolean => {
  if (!user || !clientMode) return false;

  // Simple orgs always have recipe inspiration
  if (isSimpleOrg(user)) {
    return true;
  }

  // Check for regular org
  return isFeatureFlagActive(user, FeatureFlag.FF_RECIPE_INSPIRATION);
};

// Helper function to get Recipe Inspiration route
const getRecipeInspirationRoute = (user: User | null): Routes =>
  user && isSimpleOrg(user) ? Routes.SimpleRecipeInspirationScreen : Routes.RecipeInspirationScreen;

const BottomTab = createBottomTabNavigator();
const BottomNavigationStack = (): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();

  const clientMode = useSelector(clientModeSelector);
  const user = useSelector(userSelector);

  const [subscriptionInvalid, setSubscriptionInvalid] = useState(false);

  // Check if simple organization user needs to complete onboarding
  useEffect(() => {
    if (user && clientMode && !hasUserCompletedSimpleOnboarding(user)) {
      const organisation = getOrganisation(user);

      // Skip welcome video screen if not enabled, go directly to motivation screen
      if (organisation && organisation.welcome_video_enabled !== true) {
        navigation.navigate(Routes.SimpleOnboarding_1_Motivation as never);
      } else {
        // Navigate to welcome video screen as normal
        navigation.navigate(Routes.SimpleOnboarding_0_WelcomeVideo as never);
      }
    }
  }, [user, clientMode, navigation]);

  // TODO: This should re-fetch every 30 seconds
  useEffect(() => {
    const verifySubscription = (): void => {
      const shouldUserHavePaidAccess = shouldB2CCustomerHavePaidAccess(user);
      setSubscriptionInvalid(!shouldUserHavePaidAccess);
    };

    verifySubscription();
  }, [user]);

  const invalidAccount = userAccountIsDisabledOrHasInValidSubscription(user) || subscriptionInvalid;

  const isUserDiyAndInClientMode = user ? isUserDiy(user) && clientMode : false;

  const organisation = user ? getOrganisation(user) : null;
  const areWeInAnOpinionatedRecipeInspirationApp = areWeAnOpinionatedRecipeInspirationApp(organisation || null);

  const shouldHideTab = (screenName: BottomNavigationTabOptions): boolean => {
    if (!clientMode) {
      // Ie, coach mode
      return !COACH_VIEW_SCREENS.includes(screenName);
    }
    if (isUserDiyAndInClientMode && user && !hasUserCompletedTheirDiyNutritionPlanningFormCompletely(user)) {
      if (hasUserCompletedUserProfileButDoesNotHaveWeeklyNutritionPlan(user)) {
        return screenName !== Routes.NutritionalPlanScreen;
      }
      return screenName !== Routes.DIY1GoalScreen;
    }

    if (screenName === Routes.RecipeInspirationScreen) {
      // Hide RecipeInspirationScreen for SIMPLE orgs (they should use SimpleRecipeInspirationScreen)
      if (user && isSimpleOrg(user)) {
        return true;
      }
      return user ? !isFeatureFlagActive(user, FeatureFlag.FF_RECIPE_INSPIRATION) : true;
    }

    if (screenName === Routes.SimpleRecipeInspirationScreen) {
      // Only show SimpleRecipeInspirationScreen for SIMPLE orgs
      if (!user || !isSimpleOrg(user)) {
        return true;
      }
      return user ? !isFeatureFlagActive(user, FeatureFlag.FF_RECIPE_INSPIRATION) : true;
    }

    if (screenName === Routes.MyProgressScreen) {
      // Hide Progress tab for SIMPLE orgs (they should use Recipe Inspiration instead)
      if (!shouldShowProgressTab(user)) {
        return true; // Hide the tab
      }

      if (isFeatureFlagActive(user, FeatureFlag.FF_FEMALE_HORMONES)) {
        return false;
      }

      if (areWeInAnOpinionatedRecipeInspirationApp) {
        return true;
      }
    }
    return !CLIENT_VIEW_SCREENS.includes(screenName);
  };

  if (!user) {
    console.error("user is not set in BottomNavigationStack - this should not happen");
    return <></>;
  }

  const getScreenOptions = (screenName: string): BottomTabNavigationOptions => ({
    tabBarButton: ({ onPress, accessibilityState }) => (
      <CustomTabButton
        focused={accessibilityState?.selected ?? false}
        screenName={screenName as BottomNavigationTabOptions}
        title={getTitleForTab(screenName as BottomNavigationTabOptions, t)}
        onPress={onPress ?? (() => {})}
      />
    ),
    headerShown: false,
    tabBarStyle: {
      backgroundColor: theme.colors.primary["10"],
      borderTopWidth: 1,
      borderTopColor: theme.colors.gray["100"],
      elevation: 8, // Android shadow
      shadowColor: theme.colors.gray["900"], // iOS shadow
      shadowOffset: {
        width: 0,
        height: -2,
      },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      paddingTop: 4,
      paddingBottom: Math.max(4, insets.bottom), // Use safe area bottom or minimum 4px
      height: 70 + insets.bottom, // Adjust height to include safe area
    },
    tabBarTestID: `bottomTab-${screenName}`,
  });

  let initialRouteName;
  if (invalidAccount) {
    initialRouteName = Routes.BillingIssueScreen;
  } else if (clientMode) {
    if (isUserDiyAndInClientMode) {
      if (hasUserCompletedUserProfileButDoesNotHaveWeeklyNutritionPlan(user)) {
        initialRouteName = Routes.NutritionalPlanScreen;
      } else if (!hasUserCompletedTheirDiyNutritionPlanningFormCompletely(user)) {
        initialRouteName = Routes.DIY1GoalScreen;
      } else {
        // Check for Recipe Inspiration availability
        initialRouteName = isRecipeInspirationAvailable(user, clientMode)
          ? getRecipeInspirationRoute(user)
          : Routes.DiaryViewScreen;
      }
    } else {
      // Check for Recipe Inspiration availability for non-DIY users
      initialRouteName = isRecipeInspirationAvailable(user, clientMode)
        ? getRecipeInspirationRoute(user)
        : Routes.DiaryViewScreen;
    }
  } else {
    initialRouteName = Routes.CoachModeViewClientsScreen;
  }

  return (
    // NOTE: This is a known bug due to a react version update: https://stackoverflow.com/questions/71788254/react-18-typescript-children-fc/71809927#71809927
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <BottomTab.Navigator
      screenOptions={({ route: { name: nameAsString } }) => {
        const name = nameAsString as BottomNavigationTabOptions;

        // if help screen then display
        if (name === Routes.MyProfileScreen) {
          return getScreenOptions(name);
        }

        if (invalidAccount && !DELINQUENT_SUBSCRIPTION_SCREENS.includes(name)) {
          return doNotDisplayTab;
        }

        if (shouldHideTab(name) || (!invalidAccount && DELINQUENT_SUBSCRIPTION_SCREENS.includes(name))) {
          return doNotDisplayTab;
        }

        return getScreenOptions(name);
      }}
      initialRouteName={initialRouteName}
    >
      {/* Recipe Inspiration screens FIRST (when available) */}
      <BottomTab.Screen name={Routes.RecipeInspirationScreen} component={RecipeInspirationScreen} />
      <BottomTab.Screen name={Routes.SimpleRecipeInspirationScreen} component={SimpleRecipeInspirationScreen} />

      {/* Then Progress/Diary */}
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <BottomTab.Screen name={Routes.DiaryViewScreen} component={DiaryViewScreen as any} />

      {/* DIY screens */}
      <BottomTab.Screen name={Routes.DIY1GoalScreen} component={DIY1GoalScreen} />
      <BottomTab.Screen name={Routes.NutritionalPlanScreen} component={NutritionalPlanScreen} />

      {/* Rest of screens */}
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <BottomTab.Screen name={Routes.GroceryListScreen} component={GroceryListScreen as any} />
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <BottomTab.Screen name={Routes.CoachModeViewClientsScreen} component={CoachModeViewClientsScreen as any} />
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <BottomTab.Screen name={Routes.CoachModeAccountScreen} component={CoachModeAccountScreen as any} />
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <BottomTab.Screen name={Routes.BillingIssueScreen} component={BillingIssueScreen as any} />
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <BottomTab.Screen name={Routes.MyProgressScreen} component={MyProgressScreen as any} />
      {/* NOTE: This is out of scope for the initial release */}
      {/* <BottomTab.Screen name={Routes.RecipesScreen} component={RecipeInspirationScreen} /> */}
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <BottomTab.Screen name={Routes.MyProfileScreen} component={MyProfileScreen as any} />
    </BottomTab.Navigator>
  );
};

export default BottomNavigationStack;
